import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CalendarIcon, ChevronRightIcon, RecycleIcon, HomeIcon, TruckIcon, Trash2Icon, PhoneIcon, UsersIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_SCENE = 'https://cdn.magicpatterns.com/patterns/generated-images/04ca39e3-68d4-4be2-977a-0dac93b65c94.jpg';
const PHOTO_CONTAINER = 'https://cdn.magicpatterns.com/patterns/generated-images/42283f42-4328-471a-814a-38b21dee0cd6.jpg';
function CalendarCard({
  t
}: {
  t: (fr: string, en: string) => string;
}) {
  const rows = [{
    color: 'bg-[#3A3A3A]',
    label: t('Ordures', 'Garbage'),
    date: t('Jeudi 14 mai', 'Thursday, May 14')
  }, {
    color: 'bg-[#4F46E5]',
    label: t('Recyclage', 'Recycling'),
    date: t('Jeudi 21 mai', 'Thursday, May 21')
  }, {
    color: 'bg-[#8A5A3B]',
    label: t('Matières organiques', 'Organics'),
    date: t('Jeudi 28 mai', 'Thursday, May 28')
  }];
  return <div className="w-[270px] rounded-2xl border border-[#EDEEF3] bg-white p-4 shadow-[0_10px_30px_rgba(18,18,18,0.07)]" data-id="element-1678" data-mp="2isdqdu5u65ck">
      <div className="flex items-center gap-3" data-id="element-1679" data-mp="2es3ho9qnt0u0">
        <span className="w-9 h-9 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-1680" data-mp="29s7egcpsddfi">
          <CalendarIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-1681" data-mp="29qe86yj34v81" />
        </span>
        <span className="font-sans text-[14.5px] font-medium text-[#121212]" data-id="element-1682" data-mp="32h5wzaxtmhlc">
          {t('Prochaines collectes', 'Upcoming collections')}
        </span>
      </div>
      <ul className="mt-3 space-y-1.5" data-id="element-1683" data-mp="2xfysbkqz5yiw">
        {rows.map(r => <li key={r.label} className="flex items-center gap-3 rounded-xl border border-[#F1F2F5] px-3 py-2.5" data-id="element-1684" data-mp="2shnivb4ldq17">
            <span className={`w-4 h-6 shrink-0 rounded-[3px] ${r.color}`} aria-hidden="true" data-id="element-1685" data-mp="1m47ovg47nt2i" />
            <span className="min-w-0 flex-1" data-id="element-1686" data-mp="1h2rz5ar38yds">
              <span className="block font-sans text-[12.5px] text-[#121212] leading-tight" data-id="element-1687" data-mp="1ikpay3w6eyob">{r.label}</span>
              <span className="block font-sans text-[12px] text-[#5B6478] leading-tight mt-0.5" data-id="element-1688" data-mp="1njjs71ehwf6b">{r.date}</span>
            </span>
            <ChevronRightIcon className="w-4 h-4 text-[#9AA0AE]" aria-hidden="true" data-id="element-1689" data-mp="19izbqbw1yvfz" />
          </li>)}
      </ul>
    </div>;
}
function TrackingCard({
  t
}: {
  t: (fr: string, en: string) => string;
}) {
  return <div className="relative w-[330px] h-[190px] rounded-2xl overflow-hidden border border-white/70 bg-[#F4F5F3] shadow-[0_10px_30px_rgba(18,18,18,0.10)]" data-id="element-1690" data-mp="zso8krk70qa5">
      <svg viewBox="0 0 330 190" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-1691" data-mp="qu2q0weq1b9d">
        <path d="M170 -10 C 160 70, 210 120, 190 200" stroke="#D4E3EE" strokeWidth="14" fill="none" data-id="element-1692" data-mp="uummdxrh0aug" />
        <path d="M0 70 L 330 50 M 0 160 L 330 172 M 60 0 L 100 190 M 270 0 L 250 190" stroke="#FFFFFF" strokeWidth="4" fill="none" data-id="element-1693" data-mp="gutqlo1374fm" />
        <path d="M46 140 C 70 130, 110 90, 140 84 C 170 96, 200 124, 230 128 C 256 132, 276 146, 296 150" stroke="#4F46E5" strokeWidth="2.5" fill="none" data-id="element-1694" data-mp="ltwuv698t6mj" />
        {[{
        x: 88,
        y: 116
      }, {
        x: 212,
        y: 124
      }, {
        x: 240,
        y: 130
      }].map(p => <circle key={p.x} cx={p.x} cy={p.y} r="3.5" fill="#FFFFFF" stroke="#4F46E5" strokeWidth="1.8" data-id="element-1695" data-mp="c6uajjedqjrl" />)}
      </svg>
      <span className="absolute left-[14%] top-[74%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(18,18,18,0.12)] flex items-center justify-center" data-id="element-1696" data-mp="777u71398nek">
        <HomeIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-1697" data-mp="g8z1gadg12aj" />
      </span>
      <span className="absolute left-[42.5%] top-[44%] -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#4F46E5] border-2 border-white shadow-[0_2px_8px_rgba(79,70,229,0.3)] flex items-center justify-center" data-id="element-1698" data-mp="uxv6dyk7dz6x">
        <TruckIcon className="w-4 h-4 text-white" strokeWidth={2} aria-hidden="true" data-id="element-1699" data-mp="13z1q0gi5fq4e" />
      </span>
      <span className="absolute left-[90%] top-[79%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(18,18,18,0.12)] flex items-center justify-center" data-id="element-1700" data-mp="zxg64b776bkp">
        <Trash2Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-1701" data-mp="2yd6x9jqn03g9" />
      </span>
      <div className="absolute right-3 top-3 flex items-center gap-3 rounded-xl bg-white px-3.5 py-2.5 shadow-[0_4px_14px_rgba(18,18,18,0.08)]" data-id="element-1702" data-mp="33602e8q0pdsv">
        <span data-id="element-1703" data-mp="385uqk4ierpa8">
          <span className="flex items-center gap-2 font-sans text-[12.5px] font-medium text-[#121212]" data-id="element-1704" data-mp="2agjbxglyk6za">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-1705" data-mp="2ff4brexason7" />
            {t('Camion de recyclage', 'Recycling truck')}
          </span>
          <span className="block font-sans text-[11.5px] text-[#5B6478] mt-0.5 pl-3.5" data-id="element-1706" data-mp="2kgjzxfurvb9t">
            {t('En cours de collecte', 'Collection in progress')}
          </span>
        </span>
        <ChevronRightIcon className="w-4 h-4 text-[#9AA0AE]" aria-hidden="true" data-id="element-1707" data-mp="3vt2v2k80iub7" />
      </div>
    </div>;
}
function Phone({
  t
}: {
  t: (fr: string, en: string) => string;
}) {
  return <div className="relative w-[240px] rounded-[38px] border-[7px] border-[#121212] bg-white px-5 pt-10 pb-6 shadow-[0_24px_50px_rgba(18,18,18,0.22)]" data-id="element-1708" data-mp="1o6kqkis62p5b">
      <span className="absolute left-1/2 top-2 -translate-x-1/2 w-20 h-5 rounded-full bg-[#121212]" aria-hidden="true" data-id="element-1709" data-mp="1ffkbpiwqpdr0" />
      <span className="block mx-auto w-8 h-1 rounded-full bg-[#E4E6EC]" aria-hidden="true" data-id="element-1710" data-mp="1ah94zh1qrjd0" />
      <p className="font-serif text-[18px] text-[#121212] text-center mt-4" data-id="element-1711" data-mp="15hw8e4l2ixx8">{t('« Où ça va ? »', '“Where does it go?”')}</p>
      <div className="relative mt-4 rounded-2xl overflow-hidden" data-id="element-1712" data-mp="10h8lnv3vh38t">
        <img src={PHOTO_CONTAINER} alt={t('Un contenant de plastique.', 'A plastic container.')} loading="lazy" className="w-full h-[128px] object-cover" data-id="element-1713" data-mp="wgesam7nuw8j" />
        {/* scan corners */}
        {['left-2 top-2 border-l-2 border-t-2', 'right-2 top-2 border-r-2 border-t-2', 'left-2 bottom-2 border-l-2 border-b-2', 'right-2 bottom-2 border-r-2 border-b-2'].map(c => <span key={c} className={`absolute w-4 h-4 border-white/90 ${c}`} aria-hidden="true" data-id="element-1714" data-mp="34zzjqe3fyg9f" />)}
      </div>
      <p className="font-sans text-[14px] font-medium text-[#121212] text-center leading-snug mt-5" data-id="element-1715" data-mp="39rrdsylt344b">
        {t('Ce contenant va dans le bac de recyclage.', 'This container goes in the recycling bin.')}
      </p>
      <span className="mx-auto mt-4 w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-1716" data-mp="30z60vz4uvrne">
        <RecycleIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-1717" data-mp="3op0cyehcpc6l" />
      </span>
      <span className="block mt-5 rounded-xl bg-[#4F46E5] py-3 font-sans text-[13px] text-white text-center" data-id="element-1718" data-mp="3snptuh1llbuz">
        {t('Voir les consignes', 'View guidelines')}
      </span>
      <span className="block mx-auto mt-4 w-24 h-1 rounded-full bg-[#121212]" aria-hidden="true" data-id="element-1719" data-mp="3jnkmu80bgfhc" />
    </div>;
}
export function CollectFinalCTA() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const t = (fr: string, en: string) => isFR ? fr : en;
  const benefits = [{
    icon: PhoneIcon,
    label: t('Moins d’appels et de demandes répétitifs', 'Fewer repetitive calls and requests')
  }, {
    icon: RecycleIcon,
    label: t('Un meilleur geste de tri', 'Better sorting decisions')
  }, {
    icon: UsersIcon,
    label: t('Des services plus simples à utiliser', 'Services that are easier to use')
  }, {
    icon: BarChart3Icon,
    label: t('Plus de capacité pour vos équipes', 'More capacity for your teams')
  }];
  return <div className="relative overflow-hidden" data-id="element-1720" data-mp="1d0vfcn4r5ars">
      {/* Photographic scene — right side, fading softly into white */}
      <div className="hidden lg:block absolute inset-y-0 right-0 w-[60%]" aria-hidden="true" data-id="element-1721" data-mp="1qsthge01q6eg">
        <img src={PHOTO_SCENE} alt="" className="w-full h-full object-cover object-[70%_center]" data-id="element-1722" data-mp="1m1tkj8qxfoby" />
        <div className="absolute inset-y-0 left-0 w-[55%] bg-gradient-to-r from-white via-white/80 to-transparent" data-id="element-1723" data-mp="ybpjn954i2on" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/70 to-transparent" data-id="element-1724" data-mp="tdny8hk85ra0" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-[minmax(0,44fr)_minmax(0,56fr)] gap-14 lg:gap-6 items-center" data-id="element-1725" data-mp="8sdui7a4w1z5">
        {/* LEFT */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5,
        ease: EASE
      }} data-id="element-1726" data-mp="hqphlzbskhmu">
          <p className="font-sans text-xs font-semibold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-1727" data-mp="rpf09ubii645">
            {t('Prêt à passer à l’action ?', 'Ready to take action?')}
          </p>
          <h2 className="font-serif text-[3rem] md:text-[3.8rem] lg:text-[4.4rem] text-[#121212] leading-[1.02] tracking-tight mt-7" data-id="element-1728" data-mp="10jjmg0fpatdc">
            {isFR ? <>
                Améliorez la
                <span className="block" data-id="element-1729" data-mp="1ahzgab644gqj">performance</span>
                <span className="block" data-id="element-1730" data-mp="1fezmcm2ypjhn">de vos collectes.</span>
              </> : <>
                Improve the
                <span className="block" data-id="element-1731" data-mp="2zz4ovok2ytlb">performance</span>
                <span className="block" data-id="element-1732" data-mp="2uxxjfw5eu6ht">of your collections.</span>
              </>}
          </h2>
          <p className="font-sans text-[18px] lg:text-[20px] text-[#5B6478] leading-relaxed mt-7 max-w-[520px]" data-id="element-1733" data-mp="2pykosp8arv4o">
            {t('Réduisez les demandes répétitives, facilitez le bon geste de tri et rendez vos services plus simples à utiliser.', 'Reduce repetitive requests, make proper sorting easier and your services simpler to use.')}
          </p>
          <Link to="/request-demo" onClick={() => trackEvent('Demo CTA Clicked', {
          location: 'Collect Final'
        })} className="inline-flex items-center gap-3 mt-9 rounded-lg bg-[#4F46E5] px-10 py-5 font-sans text-[17px] text-white transition-colors hover:bg-[#4338CA]" data-id="element-1734" data-mp="38xgg9ssrtvet">
            {t('Demander une démo', 'Request a demo')}
            <ArrowRightIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" data-id="element-1735" data-mp="3f3ttggpshzp3" />
          </Link>

          <ul className="mt-12 grid grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-0" data-id="element-1736" data-mp="3b289ycir2n5x">
            {benefits.map(({
            icon: Icon,
            label
          }, i) => <li key={label} className={`flex items-center gap-3 xl:px-4 ${i === 0 ? 'xl:pl-0' : 'xl:border-l xl:border-[#E4E6EC]'}`} data-id="element-1737" data-mp="3p317750frwcq" data-mpi={i}>
                <span className="w-12 h-12 shrink-0 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-1738" data-mp="1wi832qbe3hs">
                  <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1739" data-mp="3u01871ifi8wz" />
                </span>
                <span className="font-sans text-[13.5px] text-[#121212] leading-snug" data-id="element-1740" data-mp="bmbku7270onm">{label}</span>
              </li>)}
          </ul>
        </motion.div>

        {/* RIGHT — desktop composition */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: EASE
      }} className="hidden lg:block relative h-[640px]" data-id="element-1741" data-mp="lj6iyouk0n1d">
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" aria-hidden="true" data-id="element-1742" data-mp="148s1gii9cyc8">
            <path d="M40 12 C 52 14, 56 22, 55 34" stroke="#A5A1EE" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-1743" data-mp="v83olr4lp40x" />
            <path d="M22 48 C 24 58, 30 64, 34 66" stroke="#A5A1EE" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-1744" data-mp="gj7abcbyaurv" />
            <path d="M68 44 C 72 52, 72 56, 70 62" stroke="#A5A1EE" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-1745" data-mp="lc0fg1bc054h" />
          </svg>
          {[{
          l: '40%',
          t: '12%'
        }, {
          l: '34%',
          t: '66%'
        }, {
          l: '68%',
          t: '44%'
        }].map(p => <span key={p.l} className="absolute w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#4F46E5]" style={{
          left: p.l,
          top: p.t
        }} aria-hidden="true" data-id="element-1746" data-mp="3tr29pvxygv7x" />)}

          <div className="absolute left-[6%] top-[4%] z-10" data-id="element-1747" data-mp="3aujsn22l1tss">
            <CalendarCard t={t} data-id="element-1748" data-mp="3as73o4yeqj68" />
          </div>
          <div className="absolute right-0 top-[8%] z-10" data-id="element-1749" data-mp="35ue0lksei5a6">
            <TrackingCard t={t} data-id="element-1750" data-mp="35suidon6pk5r" />
          </div>
          <div className="absolute left-[40%] top-[26%] z-20 rotate-[8deg]" data-id="element-1751" data-mp="30x5gvfrltt6t">
            <Phone t={t} data-id="element-1752" data-mp="2f07kqc2fhot" />
          </div>
        </motion.div>

        {/* RIGHT — tablet / mobile */}
        <div className="lg:hidden" data-id="element-1753" data-mp="3tk8du3gmtvuy">
          <div className="relative rounded-3xl overflow-hidden" data-id="element-1754" data-mp="3tlaznwz3l840">
            <img src={PHOTO_SCENE} alt={t('Un bac de recyclage bleu devant une maison.', 'A blue recycling bin in front of a house.')} loading="lazy" className="w-full h-[440px] object-cover object-[70%_center]" data-id="element-1755" data-mp="3okvq7ghxskrb" />
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-1756" data-mp="287op8kbt6gbn">
              <div className="scale-[0.85] sm:scale-100" data-id="element-1757" data-mp="289rq5zei1tv5">
                <Phone t={t} data-id="element-1758" data-mp="nqp9gqfujw0j" />
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-5 items-center sm:items-start justify-center" data-id="element-1759" data-mp="snpfj1cp4yrn">
            <CalendarCard t={t} data-id="element-1760" data-mp="xmiuky7msxtx" />
            <div className="max-w-full overflow-hidden rounded-2xl" data-id="element-1761" data-mp="xlhak48n1102">
              <TrackingCard t={t} data-id="element-1762" data-mp="12f29byi21z0b" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}